import {
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronUp, Info, RefreshCw } from 'react-feather';
import { Sparkles } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { aiApi } from '../../../../services/api/aiApi';
import type {
  ComposeEnvelope,
  PrepSuggestionsOutput,
} from '../../../../services/api/aiTypes';
import type {
  AiPromptSegmentSelections,
  AiPromptSlot,
} from '../../../../services/api/coachSessionPrep';
import { AIPromptRow } from './AIPromptRow';
import { AINoteFooter } from './AINoteFooter';

/**
 * MED-1034 — shown on an In-Use row whose slot the current materials no longer
 * yield (a fresh suggestion returned "" for it). Keeps the coach's selection
 * visible/toggleable while reconciling it with the "no usable prompt" AI note.
 */
const STALE_SELECTION_NOTE =
  'No longer in the session materials — still in use; review or remove.';

// MED-1098 — the section's container, styled like the agenda's "Step Content" /
// "What participants see" boxes (same 1px border + radius 8 + 12px 14px padding)
// but with the AI surface's light-purple identity.
const AI_BOX_BG = '#faf9fc';
const AI_BOX_BORDER = '#ece5ff';

/**
 * MED-1034 — true when the coach has this slot In Use with persisted text but the
 * latest recipe fetch returned an empty extract for it. That is exactly the
 * combination that used to render an In-Use row contradicting the AI note.
 */
function isSlotStale(
  persistedText: string | undefined,
  inUse: boolean | undefined,
  freshText: string
): boolean {
  return (
    Boolean(inUse) &&
    (persistedText ?? '').trim() !== '' &&
    freshText.trim() === ''
  );
}

/**
 * MED-876 AC2 — per-step AI Suggestions section.
 *
 * Per AC2 the section is auto-collapsed on page load. On first expand, fires
 * POST /api/ai/prep-suggestions for this segment and caches the response.
 * Subsequent expand/collapse is purely UI state — no re-fetch unless the
 * user explicitly clicks Retry on a fail_open envelope.
 *
 * Visual reference: screenshots #4 (collapsed) and #2 (expanded).
 */
export interface AIPromptSuggestionsSectionProps {
  sessionId: string;
  /** First group's id by default — drives which cohort the recipe normalizes against. */
  groupId?: string;
  segmentIndex: number;
  /** The segment block_key used for persistence (matches Leader Notes block_key). Kept for parent traceability; this component renders without it. */
  segmentKey?: string;
  /** Current persisted selections for THIS segment — drives the In Use badges. */
  currentSelections: AiPromptSegmentSelections | undefined;
  /** Called when the coach toggles In Use / Use This — parent persists the doc. */
  onSelectionsChange: (next: AiPromptSegmentSelections) => Promise<void> | void;
  /**
   * MED-1098 — DEV preview only: a pre-seeded suggestions envelope. When set, the
   * section renders it directly instead of calling the AI backend (the dev
   * preview has no `POST /api/ai/prep-suggestions`), so the purple prompt cards +
   * AI note are reviewable in `/dev/session-prep/coach`. Undefined in production.
   */
  previewEnvelope?: ComposeEnvelope<PrepSuggestionsOutput>;
}

export function AIPromptSuggestionsSection({
  sessionId,
  groupId,
  segmentIndex,
  currentSelections,
  onSelectionsChange,
  previewEnvelope,
}: AIPromptSuggestionsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [envelope, setEnvelope] =
    useState<ComposeEnvelope<PrepSuggestionsOutput> | null>(
      previewEnvelope ?? null
    );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Whether a fetch has been attempted for the current envelope state. Without
  // this, a fetch that REJECTS leaves `envelope` null and the auto-fetch effect
  // re-fires immediately in a tight loop (the Collapse height oscillates → the
  // panel appears to open/close rapidly). Set before fetching and reset on Retry.
  // Seeded true in preview so the seam below never triggers a (doomed) fetch.
  const [attempted, setAttempted] = useState(Boolean(previewEnvelope));

  const fetchSuggestions = useCallback(async (): Promise<void> => {
    setLoading(true);
    setAttempted(true);
    try {
      const env = await aiApi.prepSuggestions({
        session_id: sessionId,
        group_id: groupId,
        segment_index: segmentIndex,
      });
      setEnvelope(env);
    } catch {
      // Leave `envelope` null; `attempted` keeps the effect from re-firing, and
      // the fail-open UI below surfaces a Retry.
    } finally {
      setLoading(false);
    }
  }, [sessionId, groupId, segmentIndex]);

  // Fire once on first expand (and again only via explicit Retry).
  useEffect(() => {
    if (expanded && envelope === null && !loading && !attempted) {
      void fetchSuggestions();
    }
  }, [expanded, envelope, loading, attempted, fetchSuggestions]);

  /** Count of In Use slots within this segment — used by the collapsed header. */
  const inUseCount =
    (currentSelections?.purpose?.in_use ? 1 : 0) +
    (currentSelections?.speaking?.in_use ? 1 : 0) +
    (currentSelections?.stall_recovery?.in_use ? 1 : 0);

  /** When the user clicks a Primary row, toggle the In Use boolean for that slot. */
  const toggleSlot = useCallback(
    async (
      slotKey: 'purpose' | 'speaking' | 'stall_recovery',
      defaultText: string
    ): Promise<void> => {
      const prev = currentSelections?.[slotKey];
      const next: AiPromptSlot = prev?.in_use
        ? { ...prev, in_use: false }
        : {
            in_use: true,
            text: prev?.text || defaultText,
            alternative_angle: prev?.alternative_angle ?? null,
          };
      const updated: AiPromptSegmentSelections = {
        ...currentSelections,
        [slotKey]: next,
      };
      setSaving(true);
      try {
        await onSelectionsChange(updated);
      } finally {
        setSaving(false);
      }
    },
    [currentSelections, onSelectionsChange]
  );

  const isFailOpen =
    envelope?.status === 'fail_open' ||
    envelope?.result === null ||
    (attempted && envelope === null);
  const result = envelope?.result ?? null;

  return (
    // MED-1098 — the section is a single nested box (mirroring the "Step Content"
    // / "What participants see" boxes: 1px border, radius 8, 12px 14px padding).
    // The header is a full-width toggle row at the top; the suggestions + AI note
    // nest INSIDE the same box on expand, rather than spilling below it.
    <Box
      style={{
        border: `1px solid ${AI_BOX_BORDER}`,
        borderRadius: 8,
        background: AI_BOX_BG,
        padding: '12px 14px',
        marginTop: 8,
      }}
    >
      <UnstyledButton
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        // display:block — a button is inline-block by default, which leaves a
        // baseline descender gap below it (extra space at the box bottom when
        // collapsed). block removes it.
        style={{ display: 'block', width: '100%' }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
          <Group gap="xs" align="center">
            <Sparkles size={14} color="#7c4dff" />
            <Text
              size="xs"
              fw={700}
              style={{
                color: '#7c4dff',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              AI Suggestions
            </Text>
          </Group>
          <Group gap="xs" align="center">
            <Text size="xs" c="dimmed">
              {inUseCount > 0 ? `${inUseCount} in use` : 'tap to view'}
            </Text>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Stack gap="sm" style={{ paddingTop: 10 }}>
          {loading && (
            <Group justify="center" py="sm">
              <Loader size="sm" />
              <Text size="xs" c="dimmed">
                Composing AI suggestions…
              </Text>
            </Group>
          )}

          {!loading && isFailOpen && (
            <Alert
              color="gray"
              variant="light"
              icon={<Info size={14} />}
              title="AI suggestions unavailable"
            >
              <Stack gap="xs">
                <Text size="sm">
                  Write your own prompt in the Leader Notes above — the rest of
                  your prep is unaffected.
                </Text>
                <Group>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<RefreshCw size={12} />}
                    onClick={() => {
                      setEnvelope(null);
                      setAttempted(false);
                      void fetchSuggestions();
                    }}
                  >
                    Retry
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {!loading && !isFailOpen && result && (
            <>
              {/* 2026-06-11 coach review: purpose / speaking prompt / stall line are extract-or-omit
                  on the API side — an empty string means "the session materials / prior session carry
                  nothing for this", and the row is hidden rather than rendered blank. Every gate falls
                  back to the persisted selection text so a slot a coach already marked In Use stays
                  visible (and toggleable) even when a later fetch returns "" for it. */}
              {(
                currentSelections?.purpose?.text || result.purpose_statement
              ).trim() !== '' && (
                <AIPromptRow
                  label="PURPOSE"
                  text={
                    currentSelections?.purpose?.text || result.purpose_statement
                  }
                  variant="primary"
                  inUse={Boolean(currentSelections?.purpose?.in_use)}
                  staleNote={
                    isSlotStale(
                      currentSelections?.purpose?.text,
                      currentSelections?.purpose?.in_use,
                      result.purpose_statement
                    )
                      ? STALE_SELECTION_NOTE
                      : undefined
                  }
                  disabled={saving}
                  onAction={() =>
                    void toggleSlot(
                      'purpose',
                      currentSelections?.purpose?.text ||
                        result.purpose_statement
                    )
                  }
                />
              )}
              {/* MED-1066 — hide a FRESH opening prompt that only echoes the curriculum
                  (speaking_prompt_from_context === false): the coach already sees that prompt
                  on the page, so a bare lift adds nothing. A persisted In-Use selection always
                  stays visible (MED-1034), and the AI note below is unaffected. */}
              {((currentSelections?.speaking?.text ?? '').trim() !== '' ||
                (result.speaking_prompt.trim() !== '' &&
                  result.speaking_prompt_from_context !== false)) && (
                <AIPromptRow
                  label="OPENING PROMPT"
                  text={
                    currentSelections?.speaking?.text || result.speaking_prompt
                  }
                  subText={
                    currentSelections?.speaking?.alternative_angle
                      ? `Alternative angle: ${currentSelections.speaking.alternative_angle}`
                      : undefined
                  }
                  variant="primary"
                  inUse={Boolean(currentSelections?.speaking?.in_use)}
                  staleNote={
                    isSlotStale(
                      currentSelections?.speaking?.text,
                      currentSelections?.speaking?.in_use,
                      result.speaking_prompt
                    )
                      ? STALE_SELECTION_NOTE
                      : undefined
                  }
                  disabled={saving}
                  onAction={() =>
                    void toggleSlot(
                      'speaking',
                      currentSelections?.speaking?.text ||
                        result.speaking_prompt
                    )
                  }
                />
              )}
              {(
                currentSelections?.stall_recovery?.text ||
                result.stall_recovery_prompt
              ).trim() !== '' && (
                <AIPromptRow
                  label="IF GROUP STALLS"
                  text={
                    currentSelections?.stall_recovery?.text ||
                    result.stall_recovery_prompt
                  }
                  variant="primary"
                  inUse={Boolean(currentSelections?.stall_recovery?.in_use)}
                  staleNote={
                    isSlotStale(
                      currentSelections?.stall_recovery?.text,
                      currentSelections?.stall_recovery?.in_use,
                      result.stall_recovery_prompt
                    )
                      ? STALE_SELECTION_NOTE
                      : undefined
                  }
                  disabled={saving}
                  onAction={() =>
                    void toggleSlot(
                      'stall_recovery',
                      currentSelections?.stall_recovery?.text ||
                        result.stall_recovery_prompt
                    )
                  }
                />
              )}
              {/* MED-1087 — the Alternative Prompt suggestion is removed: its only
                  interactive behavior ("Use This") overwrote the Opening Prompt's text
                  (data loss). The Opening Prompt, IF GROUP STALLS, and AI note are
                  unchanged. (The recipe may still emit alternative_prompts; we no longer
                  surface them.) */}
              <AINoteFooter
                text={result.ai_note.text}
                sources={result.ai_note.sources}
              />
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default AIPromptSuggestionsSection;
